import * as XLSX from 'xlsx';
import { MenuData } from '../types';

// --- Constants ---
const YELLOW_HEX = "FFF200";
const BLACK_HEX = "000000";
const WHITE_HEX = "FFFFFF";
const PURPLE_DARK_HEX = "7030A0";
const PURPLE_LIGHT_HEX = "BF7AF2";

const borderThin = { style: "thin", color: { rgb: BLACK_HEX } };
const borderMedium = { style: "medium", color: { rgb: BLACK_HEX } };

const baseStyle = {
  font: { name: "Arial", sz: 10, color: { rgb: BLACK_HEX } },
  alignment: { vertical: "center", wrapText: false }, 
  border: { top: borderThin, bottom: borderThin, left: borderThin, right: borderThin }
};

// --- Template 1 Generator (Existing Logic) ---
export const generateTemplate1Worksheet = (data: MenuData, currency: string): XLSX.WorkSheet => {
  // --- Specific Styles ---
  const styles = {
    header: {
      ...baseStyle,
      font: { ...baseStyle.font, bold: true, sz: 11, color: { rgb: WHITE_HEX } }, // White Text
      fill: { fgColor: { rgb: BLACK_HEX } }, // Black BG
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium }
    },
    categoryStart: {
      // Category Name Cell: Yellow BG, Borders, Bold
      font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true },
      alignment: { vertical: "center", wrapText: false },
      border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium },
      fill: { fgColor: { rgb: YELLOW_HEX } }
    },
    categoryEmpty: {
      // Empty Category Space: No Borders, White BG
      font: { name: "Arial", sz: 11 },
      alignment: { vertical: "center", wrapText: false },
      border: {}, // Explicitly no borders
      fill: { fgColor: { rgb: WHITE_HEX } }
    },
    categoryMiddle: {
      // Yellow BG, Top/Bottom Borders only (for continuous bar)
      font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true },
      alignment: { vertical: "center", wrapText: false },
      border: { top: borderMedium, bottom: borderMedium },
      fill: { fgColor: { rgb: YELLOW_HEX } }
    },
    categoryEnd: {
      // Yellow BG, Top/Bottom/Right Borders (closes the bar)
      font: { name: "Arial", sz: 11, color: { rgb: BLACK_HEX }, bold: true },
      alignment: { vertical: "center", wrapText: false },
      border: { top: borderMedium, bottom: borderMedium, right: borderMedium },
      fill: { fgColor: { rgb: YELLOW_HEX } }
    },
    // Item Name (White, Centered, Vertically Merged, With Borders)
    itemName: {
      ...baseStyle,
      fill: { fgColor: { rgb: WHITE_HEX } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false }
    },
    // Description & Price Item Row (White Background)
    itemDescYellow: {
      ...baseStyle,
      fill: { fgColor: { rgb: WHITE_HEX } }
    },
    // Choice Group Header (Black BG, White Text)
    groupHeaderLeft: {
      ...baseStyle,
      font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } },
      fill: { fgColor: { rgb: BLACK_HEX } },
      alignment: { horizontal: "left", vertical: "center" }
    },
    groupHeaderRight: {
      ...baseStyle,
      font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } },
      fill: { fgColor: { rgb: BLACK_HEX } },
      alignment: { horizontal: "right", vertical: "center" }
    },
    groupHeaderCenter: {
      ...baseStyle,
      font: { ...baseStyle.font, bold: true, color: { rgb: WHITE_HEX } },
      fill: { fgColor: { rgb: BLACK_HEX } },
      alignment: { horizontal: "center", vertical: "center" }
    },
    groupHeaderEmpty: {
      ...baseStyle,
      fill: { fgColor: { rgb: BLACK_HEX } }
    },
    // Option Row (White BG)
    option: {
      ...baseStyle,
      fill: { fgColor: { rgb: WHITE_HEX } }
    }
  };

  const headers = [
    'Category - EN', 'Category - AR', 
    'Item Name - EN', 'Item Name - AR', 
    'Description - EN', 'Description - AR', 
    'Item Note - EN', 'Item Note - AR',
    'Item Price', 'Max', 'Min'
  ];

  const dataRows: any[][] = [];
  const merges: any[] = [];
  const cellStyles: Record<string, any> = {};

  const setStyle = (r: number, c: number, style: any) => {
    cellStyles[`${r}_${c}`] = style;
  };

  // 1. Add Header
  dataRows.push(headers);
  headers.forEach((_, c) => setStyle(0, c, styles.header));

  let r = 1;

  data.categories.forEach(cat => {
    // 1. Category Header Row (Always separate now)
    const catRow = Array(11).fill('');
    catRow[0] = cat.name.en;
    catRow[1] = cat.name.ar || '';
    dataRows.push(catRow);
    
    // Style Category Name Cells (Yellow, Borders)
    setStyle(r, 0, { ...styles.categoryStart, alignment: { ...styles.categoryStart.alignment, horizontal: 'left' } });
    setStyle(r, 1, { ...styles.categoryStart, alignment: { ...styles.categoryStart.alignment, horizontal: 'right' } });
    
    // Style Empty Cells C-K (Yellow, Borders)
    for(let c=2; c<=10; c++) {
        if (c <= 9) setStyle(r, c, styles.categoryMiddle); // C-J: No vertical borders
        else setStyle(r, c, styles.categoryEnd); // K: Right border only
    }
    
    r++; // Move to next row

    if (cat.items.length > 0) {
       cat.items.forEach((item, iIdx) => {
          // Calculate Item Height for Vertical Merge
          let itemRows = 1;
          item.choiceGroups.forEach(cg => {
             itemRows += 1 + cg.options.length;
          });
          
          const itemStartRow = r;
          const itemEndRow = r + itemRows - 1;

          // Merge Item Name Cols (C & D) Vertically
          merges.push({ s: { r: itemStartRow, c: 2 }, e: { r: itemEndRow, c: 2 } });
          merges.push({ s: { r: itemStartRow, c: 3 }, e: { r: itemEndRow, c: 3 } });

          // --- Item Row Data ---
          const row = Array(11).fill('');
          // row[0] and row[1] are empty (Category is above)
          row[2] = item.name.en;
          row[3] = item.name.ar || '';
          row[4] = item.description.en;
          row[5] = item.description.ar || '';
          row[6] = item.note?.en || '';
          row[7] = item.note?.ar || '';
          // USE DYNAMIC CURRENCY
          row[8] = `${currency} ${item.price.toFixed(2)}`;

          dataRows.push(row);

          // Styles for Item Row
          // A & B are empty (No Borders, White BG)
          setStyle(r, 0, styles.categoryEmpty);
          setStyle(r, 1, styles.categoryEmpty);

          setStyle(r, 2, styles.itemName); // Yellow + Border
          setStyle(r, 3, styles.itemName); // Yellow + Border
          setStyle(r, 4, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'left' } });
          setStyle(r, 5, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'right' } });
          setStyle(r, 6, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'left' } });
          setStyle(r, 7, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'right' } });
          setStyle(r, 8, { ...styles.itemDescYellow, alignment: { ...styles.itemDescYellow.alignment, horizontal: 'center' } });
          setStyle(r, 9, styles.itemDescYellow);
          setStyle(r, 10, styles.itemDescYellow);

          r++;

          // --- Choice Groups ---
          item.choiceGroups.forEach(cg => {
             // Header Row (Black Bar)
             const hRow = Array(11).fill('');
             hRow[4] = cg.name.en; // "Add-ons"
             hRow[5] = cg.name.ar || 'إضافات'; 
             
             // Add Min/Max
             hRow[9] = cg.max;
             hRow[10] = cg.min;

             dataRows.push(hRow);
             
             // Styles
             setStyle(r, 0, styles.categoryEmpty); // No Border
             setStyle(r, 1, styles.categoryEmpty); // No Border
             setStyle(r, 2, styles.itemName); // Covered by merge
             setStyle(r, 3, styles.itemName);
             
             // E: Black Left, F: Black Right
             setStyle(r, 4, styles.groupHeaderLeft);
             setStyle(r, 5, styles.groupHeaderRight);
             
             // G-I: Black Empty
             setStyle(r, 6, styles.groupHeaderEmpty);
             setStyle(r, 7, styles.groupHeaderEmpty);
             setStyle(r, 8, styles.groupHeaderEmpty);
             
             // J-K: Black Center (Max/Min)
             setStyle(r, 9, styles.groupHeaderCenter);
             setStyle(r, 10, styles.groupHeaderCenter);
             
             r++;

             // Options
             cg.options.forEach(opt => {
                const oRow = Array(11).fill('');
                oRow[4] = opt.name.en;
                oRow[5] = opt.name.ar || '';
                // USE DYNAMIC CURRENCY
                oRow[8] = opt.priceModifier ? `${currency} ${opt.priceModifier.toFixed(2)}` : `${currency} -`;

                dataRows.push(oRow);

                setStyle(r, 0, styles.categoryEmpty); // No Border
                setStyle(r, 1, styles.categoryEmpty); // No Border
                setStyle(r, 2, styles.itemName);
                setStyle(r, 3, styles.itemName);
                
                // Option Data (White) - Standard Alignment
                setStyle(r, 4, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'left' } });
                setStyle(r, 5, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'right' } });
                setStyle(r, 6, styles.option);
                setStyle(r, 7, styles.option);
                setStyle(r, 8, { ...styles.option, alignment: { ...styles.option.alignment, horizontal: 'center' } });
                setStyle(r, 9, styles.option);
                setStyle(r, 10, styles.option);

                r++;
             });
          });
       });
    }
  });

  const ws = XLSX.utils.aoa_to_sheet(dataRows);
  ws['!merges'] = merges;
  
  Object.keys(cellStyles).forEach(key => {
     const [row, col] = key.split('_').map(Number);
     const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
     if(!ws[cellRef]) XLSX.utils.sheet_add_aoa(ws, [['']], { origin: cellRef });
     if(ws[cellRef]) ws[cellRef].s = cellStyles[key];
  });

  ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, // Cat
      { wch: 25 }, { wch: 25 }, // Item
      { wch: 45 }, { wch: 40 }, // Desc
      { wch: 20 }, { wch: 20 }, // Note
      { wch: 15 }, { wch: 10 }, { wch: 10 }
  ];

  return ws;
};

// --- Template 2 Generator (New Spec) ---
export const generateTemplate2Worksheet = (data: MenuData, currency: string): XLSX.WorkSheet => {
  const dataRows: any[][] = [];
  const merges: any[] = [];
  const cellStyles: Record<string, any> = {};

  const setStyle = (r: number, c: number, style: any) => {
    cellStyles[`${r}_${c}`] = style;
  };

  // --- Styles ---
  const vendorBarStyle = {
    fill: { fgColor: { rgb: "7030A0" } }, // Dark Purple
    font: { name: "Calibri", sz: 11, color: { rgb: WHITE_HEX } },
    alignment: { horizontal: "left", vertical: "center" },
    border: { top: borderMedium, bottom: borderMedium, left: borderMedium, right: borderMedium }
  };

  const headerStyle = {
    fill: { fgColor: { rgb: "BF7AF2" } }, // Purple
    font: { name: "Arial Narrow", sz: 10, bold: true, color: { rgb: WHITE_HEX } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: { bottom: borderThin } // Base border, specific overrides below
  };

  const dataStyleBase = {
    font: { name: "Arial", sz: 10, color: { rgb: BLACK_HEX } },
    alignment: { vertical: "center", wrapText: false },
    border: { bottom: borderThin, left: borderThin, right: borderThin } // Top none
  };

  // Row 1: Vendor/Branch
  const vendorName = `VENDOR NAME: ${data.title?.en || 'Twinky'}`;
  const branchName = `BRANCH: All the Branch`;
  
  const row1 = Array(12).fill('');
  row1[0] = vendorName;
  row1[2] = branchName;
  dataRows.push(row1);

  // Merges for Row 1
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }); // A1:B1
  merges.push({ s: { r: 0, c: 2 }, e: { r: 0, c: 3 } }); // C1:D1

  // Style Row 1
  for (let c = 0; c < 12; c++) {
    if (c >= 4) { // E to L: No color
      setStyle(0, c, { ...vendorBarStyle, fill: { fgColor: { rgb: WHITE_HEX } } });
    } else {
      setStyle(0, c, vendorBarStyle);
    }
  }

  // Row 2 & 3: Headers
  const headers = [
    'CATEGORY - ENGLISH', 'CATEGORY - ARABIC',
    'PRODUCT NAME - ENGLISH', 'PRODUCT NAME - ARABIC',
    'DESCRIPTION - ENGLISH', 'DESCRIPTION - ARABIC',
    'ITEM NOTE - ENGLISH', 'ITEM NOTE - ARABIC',
    'PRICE', 'PREPARATION TIME',
    'ADD-ONS ENGLISH', 'ADD-ONS ARABIC'
  ];
  
  dataRows.push(headers); // Row 2
  dataRows.push(Array(12).fill('')); // Row 3 (empty for merge)

  // Merges for Headers (A2:A3, B2:B3, ... L2:L3)
  for (let c = 0; c < 12; c++) {
    merges.push({ s: { r: 1, c: c }, e: { r: 2, c: c } });
  }

  // Style Headers
  for (let c = 0; c < 12; c++) {
    // Base Header Style
    let style = { ...headerStyle, border: { ...headerStyle.border } };
    
    // Top Border Logic
    if (c >= 4) { // E, F, G, H, I, J, K, L
      style.border.top = borderMedium;
    } else {
      // A, B, C, D - NO top border
      delete style.border.top;
    }

    // Left/Right Border Logic
    if (c === 0) style.border.left = borderMedium; // A2 Left
    if (c === 11) style.border.right = borderMedium; // L2 Right

    setStyle(1, c, style);
    setStyle(2, c, style);
  }

  // Row 4+: Data
  let r = 3;

  data.categories.forEach(cat => {
    cat.items.forEach(item => {
      // Item Row
      const row = Array(12).fill('');
      row[0] = cat.name.en;
      row[1] = cat.name.ar || '';
      row[2] = item.name.en;
      row[3] = item.name.ar || '';
      row[4] = item.description.en;
      row[5] = item.description.ar || '';
      row[6] = item.note?.en || '';
      row[7] = item.note?.ar || '';
      row[8] = `${currency} ${item.price.toFixed(2)}`;
      // J: Prep Time (Empty)
      // K, L: Add-ons (Aggregated)
      const addOnsEnParts: string[] = [];
      const addOnsArParts: string[] = [];

      item.choiceGroups.forEach(cg => {
          const groupEn = cg.name.en;
          const groupAr = cg.name.ar || cg.name.en;

          const optionsEn = cg.options.map(opt => `${opt.name.en}(${opt.priceModifier || 0})`).join(',');
          const optionsAr = cg.options.map(opt => `${opt.name.ar || opt.name.en}(${opt.priceModifier || 0})`).join(',');

          if (optionsEn) addOnsEnParts.push(`${groupEn}-${optionsEn}`);
          if (optionsAr) addOnsArParts.push(`${groupAr}-${optionsAr}`);
      });

      row[10] = addOnsEnParts.join(' / ');
      row[11] = addOnsArParts.join(' / ');

      dataRows.push(row);

      // Style Item Row
      for (let c = 0; c < 12; c++) {
        let align = 'left';
        if ([1, 3, 5, 7, 11].includes(c)) align = 'right'; // Arabic columns + L
        
        setStyle(r, c, {
          ...dataStyleBase,
          alignment: { ...dataStyleBase.alignment, horizontal: align }
        });
      }
      r++;
    });
  });

  const ws = XLSX.utils.aoa_to_sheet(dataRows);
  ws['!merges'] = merges;
  
  // Apply Styles
  Object.keys(cellStyles).forEach(key => {
     const [row, col] = key.split('_').map(Number);
     const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
     if(!ws[cellRef]) XLSX.utils.sheet_add_aoa(ws, [['']], { origin: cellRef });
     if(ws[cellRef]) ws[cellRef].s = cellStyles[key];
  });

  // Column Widths
  ws['!cols'] = [
    { wch: 20 }, // A
    { wch: 20 }, // B
    { wch: 25 }, // C
    { wch: 25 }, // D
    { wch: 50 }, // E
    { wch: 50 }, // F
    { wch: 20 }, // G (Note En)
    { wch: 20 }, // H (Note Ar)
    { wch: 15 }, // I (Price)
    { wch: 15 }, // J (Prep)
    { wch: 20 }, // K (Addons En)
    { wch: 20 }  // L (Addons Ar)
  ];

  // Row Heights
  const rows = [];
  rows[0] = { hpt: 19.5 }; // Row 1
  rows[2] = { hpt: 15.75 }; // Row 3
  ws['!rows'] = rows;

  return ws;
};

// --- Export Functions ---

export const exportTemplate1 = (data: MenuData, filename: string, currency: string) => {
    const ws = generateTemplate1Worksheet(data, currency);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Menu Data');
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportTemplate2 = (data: MenuData, filename: string, currency: string) => {
    const ws = generateTemplate2Worksheet(data, currency);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

export const exportTemplateBoth = (data: MenuData, filename: string, currency: string) => {
    const ws1 = generateTemplate1Worksheet(data, currency);
    const ws2 = generateTemplate2Worksheet(data, currency);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, 'Menu Data');
    XLSX.utils.book_append_sheet(wb, ws2, 'Sheet1');
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

// Legacy support
export const exportToExcel = exportTemplate1;

export const importFromExcel = async (file: File): Promise<MenuData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        
        // Use header:1 to get raw array of arrays
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const menuData: MenuData = { 
            title: { en: 'MENU', ar: 'قائمة الطعام' }, // Default Title
            categories: [] 
        };
        
        if (jsonData.length < 2) {
            resolve(menuData);
            return;
        }

        // --- Detect Column Mapping based on Header Row ---
        const headerRow = jsonData[0] as string[];
        const isNewFormat = headerRow[6] && headerRow[6].includes('Note');
        
        // Define indices based on format
        const IDX = {
            CAT_EN: 0,
            CAT_AR: 1,
            ITEM_EN: 2,
            ITEM_AR: 3,
            DESC_EN: 4, // Also Group Name / Option Name
            DESC_AR: 5,
            // Variable Columns
            NOTE_EN: isNewFormat ? 6 : -1, // -1 means not present
            NOTE_AR: isNewFormat ? 7 : -1,
            PRICE: isNewFormat ? 8 : 6,
            MAX: isNewFormat ? 9 : 7,
            MIN: isNewFormat ? 10 : 8
        };

        console.log("Detected Format:", isNewFormat ? "New (With Notes)" : "Legacy (No Notes)");

        let currentCat: any = null;
        let currentItem: any = null;
        let currentGroup: any = null;

        // Skip header row (index 0)
        for (let i = 1; i < jsonData.length; i++) {
          const row: any = jsonData[i];
          if (!row || row.length === 0) continue;

          const catEn = row[IDX.CAT_EN];
          const itemEn = row[IDX.ITEM_EN];
          const descOrGroupOrOptEn = row[IDX.DESC_EN];
          
          // 1. New Category
          if (catEn) {
            currentCat = {
              id: `cat-${Date.now()}-${i}`,
              name: { en: catEn, ar: row[IDX.CAT_AR] || '' },
              items: []
            };
            menuData.categories.push(currentCat);
            currentItem = null;
            currentGroup = null;
          }

          // 2. New Item
          if (itemEn && currentCat) {
            let price = 0;
            const priceVal = row[IDX.PRICE];
            if (priceVal !== undefined) {
               const priceStr = String(priceVal).replace(/[^0-9.]/g, '');
               price = parseFloat(priceStr) || 0;
            }

            currentItem = {
              id: `item-${Date.now()}-${i}`,
              name: { en: itemEn, ar: row[IDX.ITEM_AR] || '' },
              description: { en: row[IDX.DESC_EN] || '', ar: row[IDX.DESC_AR] || '' }, 
              note: { 
                  en: IDX.NOTE_EN !== -1 ? (row[IDX.NOTE_EN] || '') : '', 
                  ar: IDX.NOTE_AR !== -1 ? (row[IDX.NOTE_AR] || '') : '' 
              },
              price: price,
              choiceGroups: []
            };
            currentCat.items.push(currentItem);
            currentGroup = null;
            continue; 
          }

          // 3. Choice Group OR Option
          if (descOrGroupOrOptEn && currentItem) {
             const maxVal = row[IDX.MAX];
             const minVal = row[IDX.MIN];
             const priceVal = row[IDX.PRICE];

             const hasMax = maxVal !== undefined && maxVal !== null && String(maxVal).trim() !== '';
             const hasMin = minVal !== undefined && minVal !== null && String(minVal).trim() !== '';

             // It's a Group Header if Max/Min are present
             if (hasMax || hasMin) {
                 currentGroup = {
                     id: `cg-${Date.now()}-${i}`,
                     name: { en: descOrGroupOrOptEn, ar: row[IDX.DESC_AR] || '' },
                     min: parseInt(minVal) || 0,
                     max: parseInt(maxVal) || 1,
                     required: (parseInt(minVal) || 0) > 0,
                     optional: (parseInt(minVal) || 0) === 0,
                     options: []
                 };
                 currentItem.choiceGroups.push(currentGroup);
             } 
             // It's an Option if we are inside a group
             else if (currentGroup) {
                 let optPrice = 0;
                 if (priceVal) {
                     const pStr = String(priceVal).replace(/[^0-9.]/g, '');
                     optPrice = parseFloat(pStr) || 0;
                 }
                 
                 currentGroup.options.push({
                     id: `opt-${Date.now()}-${i}`,
                     name: { en: descOrGroupOrOptEn, ar: row[IDX.DESC_AR] || '' },
                     priceModifier: optPrice
                 });
             }
          }
        }
        resolve(menuData);
      } catch (err) {
        console.error("Import Error:", err);
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
};
